/**
 * The plan: locations, the work filed at them, and what that work produces
 * (`KLQ-20260824-001`).
 *
 * Pure, and separate from the component for the reason `features/drawings/tree.ts` is: the
 * join is the part with rules in it, and rules that live inside a `.tsx` are rules nobody can
 * exercise.
 *
 * FOUR RULES, AND WHY EACH IS NOT THE OBVIOUS ALTERNATIVE
 *
 * 1. **A deliverable hangs off the task that owns it, not off the place it sits.** That is
 *    the whole shape of the spec: the slot keeps its address in the location tree
 *    (`parent_id`, which mints its identifier) and `owning_task_id` is a *link* rendered as
 *    nesting. So the same drawing can appear beneath a task on a different floor than its
 *    own, and that is correct rather than a bug — the work is planned where the person does
 *    it, the drawing is filed where it belongs.
 * 2. **Tasks are placed at the nearest container.** A task may be anchored to a drawing
 *    (`hierarchy_node_id` is then a slot id), and a slot already renders in this view as a
 *    *deliverable* under its owning task. Drawing it a second time as a location would put
 *    one slot in two places in one table, so a task pinned to a drawing is filed under the
 *    container that holds it.
 * 3. **A container with no work anywhere beneath it is not drawn.** A seeded preset is sixty
 *    empty floors; a plan that listed all of them would bury the five rows somebody came to
 *    read. The location tree with nothing in it is the drawings screen's subject, not this
 *    one's.
 * 4. **Nothing is dropped.** A task whose node this caller's tree cannot name — no address at
 *    all, or an address their zone scope pruned — goes to *Unplaced* rather than disappearing.
 *    The two causes are deliberately one section: on this person's screen an address they
 *    cannot see and an address that does not exist are the same fact, which is exactly how
 *    `useNodeNames` already renders the register's location column.
 *
 * **This module computes no visibility.** The tree and the slot list arrive already pruned by
 * the server (`hierarchy.scope.ScopeView`) and the task pages already cut to the caller's
 * audience; filtering again here would be a second implementation of a rule that has one.
 */

import type {
  ResolvedResponsible,
  SlotNode,
  TreeNode,
} from "@/features/drawings";
import { responsibleForSlot } from "@/features/drawings";

import { bucketBy, pivotNodeId, UNASSIGNED } from "@/lib/pivot";
import type { PivotBucket, PivotGroup } from "@/lib/pivot";

import type { Task } from "./api";

/** One line of the plan. The view paints each kind; nothing here renders. */
export type PlanRow =
  | {
      readonly kind: "container";
      readonly id: string;
      readonly name: string;
      /** Work anywhere beneath it — the group header's own count. */
      readonly taskCount: number;
      /**
       * This level's **own** responsible (`KLQ-20260828-003`) — what the editor writes, and
       * `null` when the level names nobody.
       *
       * Deliberately separate from `responsible` below, which is what the cell *shows*. A
       * control that displayed an inherited value and wrote to the row it was displayed on is
       * how a person sets a value they thought they were only reading.
       */
      readonly responsibleUserId: string | null;
      /** The same question answered by the nearest-ancestor rule — own value, or inherited. */
      readonly responsible: ResolvedResponsible | null;
      /**
       * True for a heading a **pivot** invented (`KLQ-20260904-004`) — an object, a phase, a
       * state. There is no row behind it: it cannot be ticked, it names nobody, and nothing
       * about it is editable. Absent means a real level of the project.
       */
      readonly synthetic?: boolean;
    }
  | { readonly kind: "unplaced"; readonly taskCount: number }
  | {
      readonly kind: "task";
      readonly task: Task;
      /**
       * Who is responsible for the level this task sits under — shown only when the task has
       * no assignee of its own (§E). It is **not** an assignee and never becomes one: no
       * notification, no `Mine`, no row in `task_assignee` (§D).
       */
      readonly responsible: ResolvedResponsible | null;
    }
  | {
      /**
       * The end of a branch that has more work than has been fetched — the tree's own sentinel
       * (owner, 2026-09-10).
       *
       * A hierarchy has no single bottom edge: each open level is its own list. So a level that
       * ran out of fetched rows before it ran out of work draws this last, the reader scrolls
       * past it, and the branch asks for another page. It is not a control anybody has to find;
       * it is the row that makes scrolling mean the same thing inside a tree as it does in a
       * flat list.
       */
      readonly kind: "more";
      /** Which level to ask. The row's own id is derived from it and is not this. */
      readonly nodeId: string;
      /** How many are still unfetched, from the server's count. `null` when it is not known. */
      readonly remaining: number | null;
    }
  | {
      readonly kind: "deliverable";
      readonly slot: SlotNode;
      /** §C.3's order, resolved: the spatial ancestor first, then the statutory object. */
      readonly responsible: ResolvedResponsible | null;
    };

/** A node of the plan, in the shape the shared `TreeTable` renders. */
export interface PlanNode {
  readonly id: string;
  readonly row: PlanRow;
  readonly children: readonly PlanNode[];
  readonly group?: boolean;
}

/** The id `Unplaced` is keyed on. Not a hierarchy node — there is no node to name. */
export const UNPLACED_ID = "unplaced";

/** Which container a node is filed under: itself for a container, its parent for a slot. */
function containerOf(roots: readonly TreeNode[]): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  const walk = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (item.kind !== "container") continue;
      map.set(item.id, item.id);
      for (const child of item.children) {
        if (child.kind === "slot") map.set(child.id, item.id);
      }
      walk(item.children);
    }
  };
  walk(roots);
  return map;
}

/**
 * The plan, from the tree the drawings screen already fetched and the task pages this screen
 * holds.
 *
 * The task order is the server's (newest number first) and is preserved within a container
 * rather than re-sorted, for `tree.ts`'s reason: the order a list arrives in is a decision
 * somebody made server-side, and re-sorting here would make two screens disagree about it.
 */
export function buildPlan(
  roots: readonly TreeNode[],
  slots: readonly SlotNode[],
  tasks: readonly Task[],
  /**
   * Who is responsible, already resolved down each tree (`KLQ-20260828-003`). Passed in rather
   * than computed here: this module is the join between places and work, and the two maps are
   * built once per data change instead of once per plan rebuild.
   */
  byNode: ReadonlyMap<string, ResolvedResponsible> = new Map(),
  byObject: ReadonlyMap<string, ResolvedResponsible> = new Map(),
  /**
   * How much work hangs beneath each level, from the server (`KLQ-20260825-008` §5.1).
   *
   * **What it changes is rule 3, and that is the whole point.** Rule 3 drops a level with
   * nothing under it, and until this existed "nothing" meant *nothing that has arrived* — so a
   * floor whose tasks sat on page three of a paginated register was not a quiet floor, it was
   * a floor the plan **deleted**. With counts, rule 3 asks the server's number and a level
   * that holds work is drawn whether or not its work is loaded.
   *
   * It also makes the header honest: `taskCount` becomes the total rather than the loaded
   * subtotal, so `(40)` on a collapsed branch means forty and not "forty so far".
   *
   * `undefined` keeps the old behaviour exactly — a caller with no counts (the pivot's own
   * rebuild, a test) counts what it has, which is right when the whole set is what it has.
   */
  counts?: ReadonlyMap<string, number>,
  /**
   * Which levels have work that has not been fetched — each one gets a sentinel row last.
   *
   * Passed in rather than derived, because "has more" is a fact about the *fetch*: the plan can
   * see how many rows arrived and what the server says the total is, but only the fetch knows
   * whether it stopped because it ran out of work or because it ran out of page.
   */
  more?: ReadonlySet<string>,
): readonly PlanNode[] {
  const container = containerOf(roots);

  const owned = new Map<string, SlotNode[]>();
  for (const slot of slots) {
    if (slot.owningTaskId === null) continue;
    const bucket = owned.get(slot.owningTaskId);
    if (bucket === undefined) owned.set(slot.owningTaskId, [slot]);
    else bucket.push(slot);
  }

  const placed = new Map<string, Task[]>();
  const unplaced: Task[] = [];
  for (const task of tasks) {
    const at =
      task.hierarchy_node_id == null
        ? undefined
        : container.get(task.hierarchy_node_id);
    if (at === undefined) {
      unplaced.push(task);
      continue;
    }
    const bucket = placed.get(at);
    if (bucket === undefined) placed.set(at, [task]);
    else bucket.push(task);
  }

  // A task node: the work, and beneath it the drawings it owns. `${task.id}` is unique in the
  // table because a task is filed at exactly one place, and a deliverable is keyed on the
  // pair — the same slot can be owned by only one task, but keying on the pair keeps the id
  // stable if that ever stops being true.
  const taskNode = (task: Task): PlanNode => ({
    id: task.id,
    row: {
      kind: "task",
      task,
      // The level this task was filed under, not the node it is anchored to: a task pinned to
      // a drawing is placed at the container that holds it (rule 2), and inheritance follows
      // where the row is drawn rather than where it is pinned.
      responsible:
        (task.hierarchy_node_id == null
          ? undefined
          : byNode.get(container.get(task.hierarchy_node_id) ?? "")) ?? null,
    },
    children: (owned.get(task.id) ?? []).map((slot) => ({
      id: `${task.id}:${slot.id}`,
      row: {
        kind: "deliverable" as const,
        slot,
        responsible: responsibleForSlot(
          slot,
          container.get(slot.id) ?? null,
          byNode,
          byObject,
        ),
      },
      children: [],
    })),
  });

  const branch = (items: readonly TreeNode[]): readonly PlanNode[] => {
    const out: PlanNode[] = [];
    for (const item of items) {
      if (item.kind !== "container") continue;
      const below = branch(item.children);
      const here = placed.get(item.id) ?? [];
      const told = counts?.get(item.id);
      // **Rule 3, asked of the count when there is one.** Without counts this reads "has
      // anything arrived", which quietly deletes a level whose work has not — see the `counts`
      // parameter. With them it reads "does this level hold work", which is the question rule
      // 3 was always trying to ask.
      const empty =
        told === undefined ? below.length === 0 && here.length === 0 : told === 0;
      if (empty) continue;
      const taskCount =
        told ??
        here.length +
          below.reduce(
            (total, child) =>
              total + (child.row.kind === "container" ? child.row.taskCount : 0),
            0,
          );
      // The sentinel row, last, and only where the branch has more than it has fetched. The
      // caller says which levels those are; this file does not guess, because "more" is a fact
      // about the fetch and not about the tree.
      const rest = more?.has(item.id) === true
        ? [
            {
              id: `${item.id}:more`,
              row: {
                kind: "more" as const,
                nodeId: item.id,
                remaining: told === undefined ? null : told - here.length,
              },
              // A sentence about the branch, not a row of it. In the grid this changes nothing;
              // on a phone it is what stops the sentinel being drawn as a task card with an
              // empty meta line (`TreeTableNode.notice`, 2026-09-21).
              notice: true,
              children: [],
            },
          ]
        : [];
      out.push({
        id: item.id,
        row: {
          kind: "container",
          id: item.id,
          name: item.name,
          taskCount,
          responsibleUserId: item.responsibleUserId,
          responsible: byNode.get(item.id) ?? null,
        },
        group: true,
        children: [...below, ...here.map(taskNode), ...rest],
      });
    }
    return out;
  };

  const nodes = [...branch(roots)];
  // Last, and only when it holds something: an empty *Unplaced* header on every project
  // would be a section about a state nobody is in. Its count is the server's too when there
  // is one — `UNPLACED_ID` is a key in the same map, put there by the caller, because
  // "beneath nothing" is a number the endpoint reports separately for exactly this row.
  const unplacedCount = counts?.get(UNPLACED_ID) ?? unplaced.length;
  if (unplacedCount > 0) {
    nodes.push({
      id: UNPLACED_ID,
      row: { kind: "unplaced", taskCount: unplacedCount },
      group: true,
      children: unplaced.map(taskNode),
    });
  }
  return nodes;
}

/** What a selection of plan rows actually means, as work — `KLQ-20260825-006` §D. */
export interface PlanSubjects {
  /** Task ids, deduplicated, in rendered order. */
  readonly taskIds: readonly string[];
  /**
   * A container or the *Unplaced* bucket contributed to this, so the count is *the tasks of it
   * that are loaded* and never "the floor" (§D.3). `KLQ-20260821-001` §D's rule — no number on
   * this screen claims to be a total — reaches all the way into the menu's own sentence.
   */
  readonly fromContainer: boolean;
}

/** Every task drawn beneath a node, itself included when it is one. */
function tasksBeneath(node: PlanNode, out: string[]): void {
  if (node.row.kind === "task") out.push(node.row.task.id);
  for (const child of node.children) tasksBeneath(child, out);
}

/**
 * Selected rows → the tasks they mean (§D).
 *
 * This is the part ayon does not have to solve and we do, because our tree is heterogeneous:
 * one selection may hold a floor, a task, a drawing slot and the *Unplaced* header. Four
 * rules, and the fourth is the one that matters most:
 *
 *   - a **task** row is its own id;
 *   - a **deliverable** row is the task that owns the slot — which is its parent in this tree,
 *     because that is exactly what the nesting in `buildPlan` means — and nothing when the
 *     slot is drawn without one;
 *   - a **container** or the **unplaced** bucket is every task row loaded beneath it, and says
 *     so through `fromContainer`;
 *   - **a row that maps to nothing is absent, never a refusal** (§D.4). Selecting a drawing
 *     with no owning task alongside eight tasks assigns the eight. An action that refused the
 *     whole selection because part of it was structure rather than work would make the
 *     select-all people reach for first useless on every real screen.
 *
 * A row the table is no longer drawing contributes nothing: the selection is pruned to the
 * drawn grid (§B.4), and this function is a second reader of the same fact rather than a
 * second opinion about it.
 */
export function planSubjects(
  nodes: readonly PlanNode[],
  rowIds: readonly string[],
): PlanSubjects {
  const index = new Map<string, { node: PlanNode; owner: string | null }>();
  const walk = (items: readonly PlanNode[], owner: string | null): void => {
    for (const node of items) {
      index.set(node.id, { node, owner });
      walk(node.children, node.row.kind === "task" ? node.row.task.id : owner);
    }
  };
  walk(nodes, null);

  const taskIds: string[] = [];
  const seen = new Set<string>();
  let fromContainer = false;

  for (const rowId of rowIds) {
    const found = index.get(rowId);
    if (found === undefined) continue;
    const { node, owner } = found;
    const contributed: string[] = [];
    const structure = node.row.kind === "container" || node.row.kind === "unplaced";
    switch (node.row.kind) {
      case "task":
        contributed.push(node.row.task.id);
        break;
      case "deliverable":
        if (owner !== null) contributed.push(owner);
        break;
      default:
        tasksBeneath(node, contributed);
        break;
    }
    const before = taskIds.length;
    for (const id of contributed) {
      if (seen.has(id)) continue;
      seen.add(id);
      taskIds.push(id);
    }
    // Flagged by what a container *added*, not by having been clicked: a floor selected
    // alongside every task under it did not widen the count, and hedging a sentence that is
    // not hedged would teach people to ignore the word when it means something.
    if (structure && taskIds.length > before) fromContainer = true;
  }

  return { taskIds, fromContainer };
}


// --- The pivot (`KLQ-20260904-004`) -------------------------------------------

/** How the plan is grouped. `location` is the tree the project actually has. */
export type PlanAxis = "location" | "object" | "phase" | "status" | "priority";

export interface PlanPivot {
  readonly groups: readonly PivotGroup[];
  /** Which buckets this task belongs to — zero, one, or several (§B). */
  readonly keysOf: (task: Task, deliverables: readonly SlotNode[]) => readonly string[];
  /**
   * For the axes derived from a task's deliverables: which of them belong *in this bucket*.
   *
   * A task producing drawings under `SO 100` and `SO 150` is drawn under both, and under each
   * it carries the drawings that put it there — showing all of its deliverables in both places
   * would answer *"what is on SO 100"* with drawings that are not.
   */
  readonly keeps?: ((bucketId: string, slot: SlotNode) => boolean) | undefined;
  readonly unassignedLabel: string;
}

/**
 * The plan, regrouped by an axis that is not the location tree.
 *
 * **The grouping rule is `lib/pivot.ts`; what is here is this surface's rows.** Tasks are the
 * leaves — not deliverables, not levels — because a task is the unit every action on this
 * screen acts on, and a bucket of drawings whose tasks were elsewhere would be a register of
 * things nobody can close.
 *
 * **Ids are minted per bucket** (`pivotNodeId`). A task that appears under two objects is two
 * rows, and row ids are what the selection, the cell grid and the bulk endpoints are keyed on
 * — two rows sharing one id is how a fan-out silently writes half of what it says it will.
 * `planSubjects` needs no change for any of it: it indexes whatever tree it is handed, so a
 * pivoted row resolves to its task exactly as a located one does.
 */
export function pivotPlan(
  plan: readonly PlanNode[],
  pivot: PlanPivot,
): readonly PlanNode[] {
  const tasks: PlanNode[] = [];
  const walk = (nodes: readonly PlanNode[]): void => {
    for (const node of nodes) {
      if (node.row.kind === "task") tasks.push(node);
      else walk(node.children);
    }
  };
  walk(plan);

  const deliverablesOf = (node: PlanNode): SlotNode[] =>
    node.children.flatMap((child) =>
      child.row.kind === "deliverable" ? [child.row.slot] : [],
    );

  const buckets = bucketBy(tasks, {
    groups: pivot.groups,
    keysOf: (node) =>
      node.row.kind === "task"
        ? pivot.keysOf(node.row.task, deliverablesOf(node))
        : [],
    unassignedLabel: pivot.unassignedLabel,
  });

  const inBucket = (bucketId: string, node: PlanNode): PlanNode => ({
    id: pivotNodeId(bucketId, node.id),
    row: node.row,
    children: node.children
      .filter(
        (child) =>
          pivot.keeps === undefined ||
          child.row.kind !== "deliverable" ||
          bucketId === UNASSIGNED ||
          pivot.keeps(bucketId, child.row.slot),
      )
      .map((child) => ({ ...child, id: pivotNodeId(bucketId, child.id) })),
  });

  const toNode = (bucket: PivotBucket<PlanNode>): PlanNode => ({
    id: bucket.id,
    row: {
      kind: "container",
      id: bucket.id,
      name: bucket.label,
      taskCount: bucket.count,
      // A heading a pivot invented carries no responsible of its own and is not a place: both
      // are the same fact, and `synthetic` is how every consumer reads it.
      responsibleUserId: null,
      responsible: null,
      synthetic: true,
    },
    group: true,
    children: [
      ...bucket.leaves.map((node) => inBucket(bucket.id, node)),
      ...bucket.children.map(toNode),
    ],
  });

  return buckets.map(toNode);
}

/**
 * How many drawn rows will **show** a responsible that `written` is about to state, without
 * being written themselves — `KLQ-20260828-003` §F.2.
 *
 * The editor's fan-out count is the number of rows a write touches, and for this column that
 * is honest about the write and silent about the effect: naming a responsible on three
 * buildings writes three rows and changes what four hundred rows display. This is the other
 * number, and it is deliberately **not** added to the first — a person reading one total would
 * expect an undo with four hundred things in it.
 *
 * Three rules, each of them the nearest-ancestor rule read from the other end (§C):
 *
 * 1. **A container that states its own responsible blocks its whole subtree.** Nothing under
 *    it inherits from further up, so nothing under it changes.
 * 2. **A row that is itself being written is not reach.** It is a write, and it is already in
 *    the other number. A nested written container is descended into all the same: its
 *    descendants take the same value either way.
 * 3. **Only rows that actually draw the value count.** A task with an assignee shows the
 *    assignee (§E), a deliverable shows its own title, and a pivot's invented heading is not a
 *    place at all — none of them changes.
 */
export function inheritedBeneath(
  nodes: readonly PlanNode[],
  written: ReadonlySet<string>,
): number {
  if (written.size === 0) return 0;
  let total = 0;

  const walk = (items: readonly PlanNode[], under: boolean): void => {
    for (const item of items) {
      const isWritten = written.has(item.id);
      const row = item.row;
      if (under && !isWritten) {
        if (row.kind === "container") {
          // Rule 1 — and rule 3 for a synthetic heading, which is descended into but never
          // counted and never blocks: it holds no value to block with.
          if (row.synthetic !== true) {
            if (row.responsibleUserId !== null) continue;
            total += 1;
          }
        } else if (row.kind === "task" && row.task.assignee_user_ids.length === 0) {
          total += 1;
        }
      }
      walk(item.children, under || isWritten);
    }
  };

  walk(nodes, false);
  return total;
}
